// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type { IButtonStyles, ITheme } from "@fluentui/react";
// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import {
  concatStyleSets,
  getHighContrastNoAdjustStyle,
  getRTL,
  HighContrastSelector,
  memoizeFunction,
} from "@fluentui/react";

import { commonHighContrastHoverFocusStyles } from "./commonStyles";
import { controlStylingConstants } from "./controlStylingConstants";
import type { StandardControlSemanticColors } from "./standardControlSemanticColors";
import { getStandardControlSemanticColorsWithTheme } from "./standardControlSemanticColors";
import { ensureMinimumTargetSizesLegacy } from "./ensureMinimumTargetSizing";

export const chevronButtonHighContrastInvertedStyling = {
  backgroundColor: "Highlight",
  selectors: {
    "svg > path.OfficeIconColors_HighContrast": {
      fill: "HighlightText",
    },
  },
};

export const getCaretDownButtonStyles = memoizeFunction(
  (theme: ITheme, customStyles?: IButtonStyles): IButtonStyles => {
    const rtl = getRTL();
    const standardControlSemanticColors: StandardControlSemanticColors =
      getStandardControlSemanticColorsWithTheme(theme);
    const { comboBoxHeight, comboBoxCaretWidth } = controlStylingConstants;

    const defaultStyles: IButtonStyles = {
      root: [
        {
          height: comboBoxHeight,
          width: comboBoxCaretWidth,
          minWidth: comboBoxCaretWidth,
          margin: 0,
          padding: 0,
          top: -1,
          right: -1,
          border: "none",
          selectors: {
            [HighContrastSelector]: {
              border: "none",
            },
          },
        },
        // This RTL-only style needed to make the chevron icon align better in RTL
        // Mostly because our chevron icon is not exactly center aligned
        // 'right' will get turned into 'left' by Fabric internally, so it is really a 'left: 0' style
        rtl && {
          right: 0,
        },
      ],
      rootDisabled: {
        background: "transparent",
        borderColor: "transparent",
      },
      rootHovered: [
        {
          cursor: "default",
          ...getHighContrastNoAdjustStyle(),
          selectors: {
            ":hover": {
              // increase our specificity to apply the hover background color over the ComboBox root button specifier
              backgroundColor:
                standardControlSemanticColors.comboBoxButtonHoverBackgroundColor,
              selectors: {
                [HighContrastSelector]:
                  chevronButtonHighContrastInvertedStyling,
              },
            },
          },
        },
        commonHighContrastHoverFocusStyles,
      ],
      rootChecked: [
        {
          backgroundColor:
            standardControlSemanticColors.comboBoxButtonSelectedBackgroundColor,
        },
        commonHighContrastHoverFocusStyles,
      ],
      rootCheckedHovered: [
        {
          backgroundColor:
            standardControlSemanticColors.comboBoxButtonHoverBackgroundColor,
        },
        commonHighContrastHoverFocusStyles,
      ],
      rootCheckedPressed: {
        selectors: {
          ":active": [
            {
              backgroundColor:
                standardControlSemanticColors.inputPressedBackgroundColor,
            },
            commonHighContrastHoverFocusStyles,
          ],
        },
      },
      rootExpandedHovered: commonHighContrastHoverFocusStyles,
      rootExpanded: commonHighContrastHoverFocusStyles,
      rootPressed: [
        {
          selectors: {
            ":active": {
              // increase our specificity to apply the pressed background color over the ComboBox root button specifier
              backgroundColor:
                standardControlSemanticColors.inputButtonPressedBackgroundColor,
            },
          },
        },
        commonHighContrastHoverFocusStyles,
      ],
      icon: {
        margin: 0,
        display: "flex",
        height: 12,
        width: 12,
        lineHeight: 12,
        fill: standardControlSemanticColors.inputTextColor,
      },
      iconDisabled: {
        opacity: 0.4,
      },
    };

    return ensureMinimumTargetSizesLegacy(
      concatStyleSets(defaultStyles, customStyles)
    );
  }
);
